import { debounce } from "lodash";
import { useListFilterContext } from "ra-core";
import { FC, createContext, useContext, useMemo, useState } from "react";
import { RoleResponseDto, UserSearchCriterias, UserStatus } from "~/gen/api/user";


interface UsersTableSearchContextProps {
  states: {
    name: string;
    roles: RoleResponseDto[];
    status: UserStatus | undefined;
  },
  searchName: (s: string) => void;
  setSearchRole: (role: RoleResponseDto, remove: boolean) => void;
  setSearchStatus: (status: UserStatus | undefined) => void;
}

const UsersTableSearchContext = createContext<UsersTableSearchContextProps>({} as any)

export const useUsersTableSearchContext = () => useContext(UsersTableSearchContext)

const UsersTableSearchProvider: FC = ({ children }) => {
  const { setFilters, displayedFilters } = useListFilterContext()

  const [states, setStates] = useState({
    name: '',
    roles: [] as RoleResponseDto[],
    status: UserStatus.Active as UserStatus|undefined,
  })

  const ctrls = useMemo(() => {
    const invokeSearch = (s1: typeof states) => {
      const input : UserSearchCriterias = {
        name: s1.name || null,
        customRoleIds: s1.roles.filter(r => !r.isDefault).map(r => r.roleId!),
        defaultRoleIds: s1.roles.filter(r => r.isDefault).map(r => r.roleId!),
        status: s1.status,
      }
      setFilters(input, displayedFilters)
    }
    
    const debouncedInvokeSearch = debounce(invokeSearch, 300)

    return {
      searchName: (s: string) => {
        setStates(v => {
          if (v.name !== s) {
            const v1 = {...v, name: s}
            setTimeout(() => debouncedInvokeSearch(v1))
            return v1
          } else {
            return v
          }
        })
      },
      setSearchRole: (role: RoleResponseDto, remove: boolean) => {
        setStates(v => {
          const i = v.roles.findIndex(r => r.roleId === role.roleId && r.organizationId === role.organizationId)
          if (remove && i > -1) {
            const roles = [...v.roles]
            roles.splice(i, 1)
            const v1 = {...v, roles}
            setTimeout(() => invokeSearch(v1))
            return v1
          } else if (!remove && i === -1) {
            v.roles.push(role)
            const v1 = {...v}
            setTimeout(() => invokeSearch(v1))
            return v1
          } else {
            return v
          }
        })
      },
      setSearchStatus: (status: UserStatus|undefined) => {
        setStates(v => {
          if (v.status !== status) {
            const v1 = {...v, status}
            setTimeout(() => invokeSearch(v1))
            return v1
          } else {
            return v
          }
        })
      }
    }
  }, [displayedFilters, setFilters])

  return (
    <UsersTableSearchContext.Provider value={{...ctrls, states}}>
      {children}
    </UsersTableSearchContext.Provider>
  )
}

export default UsersTableSearchProvider;
